/*** IMPORTS FROM imports-loader ***/
var define = false;

/*jslint todo: true*/

"use strict";

var ko = require("knockout");
var R = require("ramda");
var Rx = require("rxjs");

var knockoutDataProvider = require("../data-provider/knockoutDataProvider");
var DataState = require("../data-provider/dataProvider").DataState;
var pli = require("../forms");
var pliObservable = require("../forms-knockoutcontrols");
var QueryOrigin = require("../data-provider/dataProvider").QueryOrigin;
var stateOfResponse = require("../data-provider/dataProvider").stateOfResponse;
var stringUtil = require("../util/string");

var Shared = require("./shared");


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// "PicklistBehaviour" class
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

/**
 * Page-local state instance.
 */
var pageState = new Shared.PageState();

/**
 * Behaviour for a picklist type.
 *
 * This behaviour will introduce a new public property to target
 * observable, "o.item". This observable will contain details of
 * the selected item (usually row array from the data source) or null.
 *
 * "o.item" observable is useful when you need more information
 * about the selected picklist item than its ID (which is stored
 * as string value of the main observable).
 */
function PicklistBehaviour() {
    return this; // keep JSLint happy
}

/**
 * Initializes the picklist behaviour.
 *
 * @this PliTextBoxObservable
 */
PicklistBehaviour.prototype.init = function picklistBehaviourInit() {
    // if no updateDelay was specified, default to 0
    if (!this.behaviourData.updateDelay) {
        this.behaviourData.updateDelay = 0;
    }

    // initialize picklist-specific properties
    this.item = ko.observable(null);
    this._displayIndex = this.behaviourData.displayValueIndex || 0;
    this._idField = Shared.getIdField(this.behaviourData);
    this._isBusy = false;
    this._isFailed = false;
    this._searchSubscription = null;

    this._onDataUpdated = PicklistBehaviour.prototype._onDataUpdated.bind(this);
    this._setItemObservable = PicklistBehaviour.prototype._setItemObservable.bind(this);

    this._dataProvider = PicklistBehaviour.prototype._getDataProvider.call(this);
    this._dataProvider
        .search(Rx.Observable.of({}), QueryOrigin.Initial)
        .map(function getOnDataUpdatedArgs(result, i) {
            return [result, i === 0 /* initialUpdate */];
        })
        .subscribe(R.apply(this._onDataUpdated)); // R.apply will transform the array from getOnDataUpdatedArgs to individual arguments

    this.subscribe(this._setItemObservable);
};

/**
 * Convert bound DOM element to a Select2 picklist + setup all necessary event handlers.
 *
 * @param controller
 *     TextBoxController instance.
 * @this PliTextBoxObservable
 */
PicklistBehaviour.prototype.initBinding = function picklistBehaviourInitBinding(controller) {
    if (controller.$input.length !== 1) { throw new Error("Expected exactly one binding target"); }

    var placeholder = (this.behaviourData && this.behaviourData.placeholder) || controller.$input.attr("placeholder");

    // Store event stream on controller. This allows target observable to
    // change while still using the same event stream.
    controller.eventStream = Shared.initializeSelect2(controller, R.merge({
        allowClear: true,
        getId: R.nthArg(1), // pull 'value' argument
        pageState: pageState,
        placeholder: placeholder
    }, this.behaviourData.picklistSelect2Options || {})).publish(); // returns connectable observable

    // TODO: when https://github.com/pandell/web-pli/issues/330 is tackled,
    // the subscribable returned from "connect()" should be disposed when
    // controller is disposed.
    controller.eventStream.connect(); // begin publishing as hot observable (will not count references)
};

/**
 * Attempt to parse an identifier value by looking it up in the current dataset.
 *
 * @param value
 *      String value loaded into the picklist.
 * @returns
 *      The matching identifier if found; otherwise, an error provider function.
 */
PicklistBehaviour.prototype.parse = function picklistBehaviourParse(value) {
    value = stringUtil.trim(value) || null; // normalize "value"
    var found = this._dataProvider.findItem(value);
    if (found.result === false) { // explicit check for "false", do not replace with falsy test
        return (found.match === knockoutDataProvider.MatchResult.NoMatch
            ? PicklistBehaviour.prototype._getErrorMessageInvalid
            : PicklistBehaviour.prototype._getErrorMessageAmbiguous);
    }

    return found.result;
};

/**
 * Display the picklist form for the current observable.
 *
 * Does not mutate state (but "picklistBehaviourOnShowDone"
 * action will mutate state in the future if user selects a
 * new value in picklist form).
 *
 * @param options
 *     Options controlling the 'show' behaviour.
 */
PicklistBehaviour.prototype.show = function picklistBehaviourShow(options) {
    var filterValue = pageState.openTextBoxQueryTerm;
    if (pageState.openTextBox !== null) {
        pageState.openTextBox.$input.select2("close");
    }

    // apply initial filtering if our observable
    // has a value that does not uniquely identify
    // a single item, but matches some items
    var behaviourData = this.behaviourData || {};
    var displayValueIndex = this._displayIndex;
    var selectedItems = [];
    var initialItem = this.item.peek();

    if (initialItem) {
        selectedItems.push(R.assoc(displayValueIndex, initialItem[displayValueIndex], {}));
    }

    // show picklist form
    var observable = this;
    pli.forms.registry.picklistForm.open({
        dataProvider: this._dataProvider,
        filterValue: filterValue,
        filterDebounceTimeout: behaviourData.filterDebounceTimeout,
        hideFilter: !!behaviourData.hideFilter,
        idField: this._idField,
        selectedItems: selectedItems,
        createItem: behaviourData.createItem || behaviourData.onNewItem || null,
        openSelectedItem: behaviourData.openSelectedItem || behaviourData.onOpenItem || null,
        previewInit: behaviourData.previewInit || null,
        containerCss: behaviourData.containerCss || null,
        value: function picklistBehaviourOnShowDone(selectedItems) {
            if (options.$input) {
                options.$input.select2("focus");
            }

            var selectedValue; // default to undefined
            if (typeof selectedItems === "object" && selectedItems.length) {
                observable.item(selectedItems[0]);
                selectedValue = selectedItems[0][displayValueIndex];
            }

            return options.onShowDone.call(options.onShowDoneContext, selectedValue);
        },
        text: behaviourData.text || null,
        view: behaviourData.view || null
    });

    return false;
};

/**
 * Set up query subscription when controller binding is updated.
 *
 * @param controller
 *      TextBoxController instance.
 */
PicklistBehaviour.prototype.updateBinding = function picklistBehaviourUpdateBinding(controller) {
    var targetObservable = this;
    if (targetObservable._searchSubscription) {
        targetObservable._searchSubscription.unsubscribe();
        targetObservable._searchSubscription = null;
    }

    var eventStream = controller.eventStream;
    if (!eventStream) {
        throw new Error("\"controller.eventStream\" must contain an observable stream of select2 events.");
    }
    targetObservable._searchSubscription = targetObservable._dataProvider
        .search(eventStream
            .filter(R.propEq("type", "query"))
            .map(function transformSelect2Options(e) {
                // See http://select2.github.io/select2/#doc-query for QueryOptions interface documentation
                var queryOptions = e.options;
                return {
                    context: R.merge(queryOptions, { pageState: pageState, rowIdIndex: targetObservable._displayIndex }),
                    currentValue: targetObservable.peekValue(),
                    filter: queryOptions.term
                };
            }))
        .subscribe(Shared.processDataProviderResponse);
};

/**
 * Validates a value supplied to a picklist.
 *
 * @param value
 *     Identifier value to validate.
 * @returns
 *     'null' (valid) if the supplied identifier value is contained
 *     in the current dataset; otherwise, returns an error provider function.
 */
PicklistBehaviour.prototype.validate = function picklistBehaviourValidate(value) {
    var found = this._dataProvider.findItem(value);
    if (found.match !== knockoutDataProvider.MatchResult.ExactMatch) {
        return (found.match === knockoutDataProvider.MatchResult.NoMatch
            ? PicklistBehaviour.prototype._getErrorMessageInvalid
            : PicklistBehaviour.prototype._getErrorMessageAmbiguous);
    }
    return null;
};

/**
 * @this PliTextBoxObservable
 */
PicklistBehaviour.prototype._getErrorMessageInvalid = pliObservable.shared.createGetErrorMessage("errorInvalidValue", "Invalid value");

/**
 * @this PliTextBoxObservable
 */
PicklistBehaviour.prototype._getErrorMessageAmbiguous = pliObservable.shared.createGetErrorMessage("errorAmbiguousValue", "More than one match");

/**
 * Gets the DataProvider instance providing us with the picklist data.
 *
 * @this PliTextBoxObservable
 * @throws
 *      If no provider was configured in "this.behaviourData"
 * @returns
 *      Knockout data provider.
 */
PicklistBehaviour.prototype._getDataProvider = function picklistBehaviourGetDataProvider() {
    var behaviourData = this.behaviourData;
    if (behaviourData) {
        var settings = {
            displayIndex: this._displayIndex,
            exactMatchIgnoresCase: behaviourData.picklistValidationIgnoresCase,
            filterLimit: behaviourData.picklistAutocompleteLimit,
            itemFilterBuilder: behaviourData.itemFilterBuilder,
            searchIndex: behaviourData.searchIndex
                || (behaviourData.view && behaviourData.view.searchIndex)
                || this._displayIndex
        };
        var source = null;

        if (behaviourData.subscribe) { // check for the subscribe function instead of ko.isObservable (to handle cross window observables)
            source = behaviourData;
        } else if (behaviourData.picklistData && behaviourData.picklistData.subscribe) {
            source = behaviourData.picklistData;
        }
        if (source) {
            return knockoutDataProvider.createKnockoutDataProvider(source, settings);
        }
    }
    throw new Error("\"behaviourData\" must contain a \"picklistData\" knockout observable.");
};

/**
 * Update the state based on changes to the underlying data source.
 *
 * Parses the value if data has been loaded.
 *
 * @param dataResult
 *      Response from the data provider.
 * @this PliTextBoxObservable
 */
PicklistBehaviour.prototype._onDataUpdated = function picklistBehaviourOnDataUpdated(dataResult, initialUpdate) {
    var updateNeeded = false;
    var state = stateOfResponse(dataResult);
    if (state === DataState.Ready) { // data is available now
        updateNeeded = (this._isFailed || this._isBusy);
        this._isFailed = false;
        this._isBusy = false;
    } else { // data is not available (either still loading or loading failed)
        var isFailed = state === DataState.Error;
        var isBusy = state === DataState.Busy;
        updateNeeded = (this._isFailed !== isFailed || this._isBusy !== isBusy);
        this._isFailed = isFailed;
        this._isBusy = isBusy;
    }

    // ensure that 'parse' and 'validate' observable methods
    // are not called during 'init'
    if (initialUpdate !== true) { // explicit check, do not replace with "falsy"
        updateNeeded = this._loadText(this.peekValue()) || updateNeeded;

        if (updateNeeded) {
            this._updateView();
            this._setItemObservable(this.peekValue());
        }
    }
};

/**
 * Sets the item observable based on a row ID value.
 *
 * @param value
 *      String containing the current observable value.
 */
PicklistBehaviour.prototype._setItemObservable = function setPicklistItemObservable(value) {
    this.item(this._dataProvider.findItem(value).item);
};


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Initialization
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

if (require("targetenv").isBrowser) {
    require("select2");

    var registerPicklistForm = require("./picklistForm").registerPicklistForm;
    var PicklistFormViewModel = require("./picklistFormViewModel");

    registerPicklistForm(PicklistFormViewModel);
}


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Public
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

module.exports = {
    behaviours: {
        picklist: new PicklistBehaviour()
    }
};




//////////////////
// WEBPACK FOOTER
// ./~/pli/forms-knockoutcontrols-picklist/knockoutControlsPicklist.js
// module id = 26
// module chunks = 49